import { Observable, of } from 'rxjs'
import { catchError, filter, flatMap, map, reduce, skipUntil, takeUntil } from 'rxjs/operators'
import { ofType, StateObservable } from 'redux-observable'
import {
  EntitySnapshotBeginAction,
  EntityStreamSnapshotsBeginAction,
  EntityStreamSnapshotsEndAction,
  EntityType,
  EntityTypeEnum,
  EntityUpdateAction,
  LogToServerActions,
  ServerActionTypes
} from 'api'
import { GlobalState } from 'store'
import { ConnectionActions } from 'store/connection'
import { Locale, Translation } from 'locale'
import { BTQAction } from 'epics'

export type EntityIntegrityMap = Record<EntityType, { snapshotSize: number; actualSize: number }>

const whenAllCountsMatch = (entityIntegrityMap: EntityIntegrityMap) =>
  Object.keys(entityIntegrityMap).some(
    (entityType: EntityType) => entityIntegrityMap[entityType].actualSize !== entityIntegrityMap[entityType].snapshotSize
  )

const removeValidSnapshots = (entityIntegrityMap: EntityIntegrityMap) =>
  Object.keys(entityIntegrityMap).reduce((acc, entityType: EntityType) => {
    if (entityIntegrityMap[entityType].actualSize !== entityIntegrityMap[entityType].snapshotSize) {
      acc[entityType] = entityIntegrityMap[entityType]
    }
    return acc
  }, {} as EntityIntegrityMap)

export function snapshotIntegrityEpic(action$: Observable<BTQAction>, _state$: StateObservable<GlobalState>, _dependencies: {}) {
  return action$.pipe(
    ofType<
      BTQAction,
      ServerActionTypes.ENTITY_SNAPSHOT_BEGIN | ServerActionTypes.ENTITY_UPDATE,
      EntitySnapshotBeginAction | EntityUpdateAction
    >(ServerActionTypes.ENTITY_SNAPSHOT_BEGIN, ServerActionTypes.ENTITY_UPDATE),
    skipUntil(
      action$.pipe(
        ofType<BTQAction, ServerActionTypes.ENTITY_STREAM_SNAPSHOTS_BEGIN, EntityStreamSnapshotsBeginAction>(
          ServerActionTypes.ENTITY_STREAM_SNAPSHOTS_BEGIN
        )
      )
    ),
    takeUntil(
      action$.pipe(
        ofType<BTQAction, ServerActionTypes.ENTITY_STREAM_SNAPSHOTS_END, EntityStreamSnapshotsEndAction>(
          ServerActionTypes.ENTITY_STREAM_SNAPSHOTS_END
        )
      )
    ),
    reduce((acc, action) => {
      if (
        action.type === ServerActionTypes.ENTITY_SNAPSHOT_BEGIN &&
        // TODO needs proper implementation
        action.payload.entityType !== EntityTypeEnum.TRANSACTION_META &&
        action.payload.entityType !== EntityTypeEnum.RESPONSE_CLUSTER_MESSAGE &&
        action.payload.entityType !== EntityTypeEnum.EGRESS_SOTW_PUBLISH &&
        action.payload.entityType !== EntityTypeEnum.SCHEDULED_TASK_STATE &&
        action.payload.entityType !== EntityTypeEnum.COMMAND &&
        action.payload.entityType !== EntityTypeEnum.RESET_SOTW &&
        action.payload.entityType !== EntityTypeEnum.SOTW_START_FROM_CHECKPOINT
      ) {
        acc[action.payload.entityType] = {
          snapshotSize: action.payload.snapshotSize,
          actualSize: 0
        }
      }
      if (action.type === ServerActionTypes.ENTITY_UPDATE) {
        if (acc[action.payload.update.entityType]) {
          acc[action.payload.update.entityType] = {
            ...acc[action.payload.update.entityType],
            actualSize: acc[action.payload.update.entityType].actualSize + 1
          }
        } else {
          throw Error(
            `ENTITY_UPDATE message received before the respective entities ENTITY_SNAPSHOT_BEGIN message for entityType: ${action.payload.update.entityType}`
          )
        }
      }
      return acc
    }, {} as EntityIntegrityMap),
    filter(whenAllCountsMatch),
    map(removeValidSnapshots),
    flatMap((entityIntegrityMap: EntityIntegrityMap) =>
      of(
        LogToServerActions.logDataIntegrityError(
          `Entity snapshot data integrity mismatch for entity type(s): ${Object.keys(entityIntegrityMap).join(',')}`,
          JSON.stringify(entityIntegrityMap)
        ),
        ConnectionActions.signOut(Locale.translate(Translation.SC_DATA_INTEGRITY_ERROR))
      )
    ),
    catchError(error => {
      return of(
        LogToServerActions.logDataIntegrityError(error.toString()),
        ConnectionActions.signOut(Locale.translate(Translation.SC_ERROR_CAUGHT))
      )
    })
  )
}
